// Shared inputs for the lead form, used by both the dark CTA section and the dialog modal.
// Owns its own state but lets the parent intercept submit via `onSubmitted`.
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API_BASE as API } from "@/lib/api";
import { BRAND_NAME } from "@/constants/site";
import { submitBoth, submitSmartInquiry } from "@/lib/inquiry";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

const initial = (preset = {}) => ({
  name: "",
  email: "",
  company: "",
  phone: "",
  use_case: preset.use_case || "",
  message: preset.message || "",
});

export const LeadFormFields = ({
  categories = [],
  preset,
  onSubmitted,
  ctaLabel = "Request demo",
  compact = false,
  testidPrefix = "",
}) => {
  const tid = (name) => `${testidPrefix}${name}`;
  const [form, setForm] = useState(() => initial(preset));
  const [submitting, setSubmitting] = useState(false);

  // If the modal opens with a new context (different feature), reset
  useEffect(() => {
    setForm(initial(preset));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset?.use_case, preset?.message]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please share your name and work email.");
      return;
    }
    setSubmitting(true);
    const useCase = categories.find((c) => (c.slug || c.id) === form.use_case);
    try {
      await submitBoth(
        axios.post(`${API}/leads`, form),
        submitSmartInquiry({
          name: form.name,
          work_email: form.email,
          company: form.company,
          phone: form.phone,
          primary_use_case: useCase?.name || form.use_case,
          description: form.message,
        })
      );
      toast.success("Got it - our team will reach out within 1 business day.");
      setForm(initial(preset));
      if (onSubmitted) onSubmitted();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid={tid("lead-form")}>
      <div className={`grid grid-cols-1 ${compact ? "" : "md:grid-cols-2"} gap-4`}>
        <Field label="Full name *">
          <Input
            data-testid={tid("form-input-name")}
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
          />
        </Field>
        <Field label="Work email *">
          <Input
            data-testid={tid("form-input-email")}
            type="email"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
          />
        </Field>
        <Field label="Company">
          <Input
            data-testid={tid("form-input-company")}
            value={form.company}
            onChange={(e) => update("company", e.target.value)}
          />
        </Field>
        <Field label="Phone">
          <Input
            data-testid={tid("form-input-phone")}
            value={form.phone}
            onChange={(e) => update("phone", e.target.value)}
          />
        </Field>
        <Field label="Primary use case" full>
          <Select value={form.use_case} onValueChange={(v) => update("use_case", v)}>
            <SelectTrigger data-testid={tid("form-select-usecase")} className="w-full">
              <SelectValue placeholder="Pick a category" />
            </SelectTrigger>
            <SelectContent>
              {categories.map((c) => (
                <SelectItem
                  key={c.id}
                  value={c.slug || c.id}
                  data-testid={tid(`form-usecase-option-${c.slug || c.id}`)}
                >
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Tell us about your environment" full>
          <Textarea
            data-testid={tid("form-input-message")}
            value={form.message}
            onChange={(e) => update("message", e.target.value)}
            placeholder="Number of cameras, sites, current pain points..."
            rows={4}
          />
        </Field>
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
        <p className="text-xs text-neutral-500">
          By submitting, you agree to be contacted by {BRAND_NAME} for this demo request.
        </p>
        <Button
          type="submit"
          data-testid={tid("form-submit")}
          disabled={submitting}
          className="bg-brand text-white hover:bg-brand-dark font-semibold px-6 py-3 h-auto"
        >
          {submitting ? "Sending..." : ctaLabel}
        </Button>
      </div>
    </form>
  );
};

const Field = ({ label, children, full }) => (
  <label className={`block ${full ? "md:col-span-2" : ""}`}>
    <span className="block font-mono-ui text-[10px] uppercase tracking-[0.18em] text-neutral-500 mb-1.5">
      {label}
    </span>
    {children}
  </label>
);
